import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add, CheckCircle, Delete, Edit, People, Refresh, Save, Star, Subscriptions, WorkspacePremium } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { deleteSubscriptionPlan, listSubscriptionPlans, saveSubscriptionPlan, setSubscriptionPlanActive } from '../../services/api/subscriptionPlanService';
import { friendlyError } from '../../utils/userMessages';
import {
  WorkspaceEmpty,
  WorkspaceHeader,
  WorkspacePage,
  WorkspacePanel,
  WorkspaceStatCard,
  StatusChip,
  alpha,
  dialogPaperSx,
  fieldSx,
  primaryButtonSx,
  quietButtonSx,
  workspacePalette as P,
} from '../common/AdminWorkspace';

const empty = { name: '', description: '', price: 0, currency: 'EUR', billingCycle: 'monthly', userLimit: 5, unlimitedUsers: false, allowExtraSeats: true, isActive: true, isPopular: false, sortOrder: 0, featuresText: '' };
const money = (value, currency = 'EUR') => {
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 2 }).format(Number(value || 0)); }
  catch { return `${currency} ${Number(value || 0).toFixed(2)}`; }
};

export default function SubscriptionPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...empty });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setPlans(await listSubscriptionPlans()); }
    catch (error) { toast.error(friendlyError(error, 'Unable to load subscription plans.')); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const active = plans.filter((plan) => plan.isActive !== false);
    const limited = active.filter((plan) => !plan.unlimitedUsers);
    return {
      active: active.length,
      limited: limited.length,
      capacity: limited.reduce((sum, plan) => sum + Number(plan.userLimit || plan.maxUsers || 0), 0),
      extraEnabled: active.filter((plan) => !plan.unlimitedUsers && plan.allowExtraSeats !== false).length,
    };
  }, [plans]);

  const startCreate = () => { setEditing(null); setForm({ ...empty, sortOrder: plans.length }); setOpen(true); };
  const startEdit = (plan) => { setEditing(plan); setForm({ ...empty, ...plan, featuresText: (plan.features || []).join('\n') }); setOpen(true); };

  const save = async () => {
    if (!String(form.name || '').trim()) { toast.error('Plan name is required.'); return; }
    if (!/^[A-Za-z]{3}$/.test(String(form.currency || ''))) { toast.error('Currency must be a 3-letter code such as EUR, USD or INR.'); return; }
    if (!form.unlimitedUsers && Number(form.userLimit || 0) < 1) { toast.error('Included user limit must be at least 1.'); return; }
    setSaving(true);
    try {
      await saveSubscriptionPlan({ ...form, id: editing?.id, createdAt: editing?.createdAt });
      toast.success(editing ? 'Plan updated.' : 'Plan created.');
      setOpen(false);
      setEditing(null);
      await load();
    } catch (error) {
      toast.error(friendlyError(error, 'Unable to save plan.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (plan) => {
    if (!window.confirm(`Delete ${plan.name}? Plans assigned to companies cannot be deleted.`)) return;
    try { await deleteSubscriptionPlan(plan.id); toast.success('Plan deleted.'); await load(); }
    catch (error) { toast.error(friendlyError(error, 'Unable to delete plan.')); }
  };

  const toggle = async (plan, active) => {
    try { await setSubscriptionPlanActive(plan.id, active); await load(); }
    catch (error) { toast.error(friendlyError(error, 'Unable to update plan.')); }
  };

  return (
    <WorkspacePage>
      <WorkspaceHeader
        eyebrow="Subscriptions & capacity"
        title="Company Subscription Plans"
        subtitle="Control package pricing, included company-user limits and whether companies may purchase additional seats."
        icon={<WorkspacePremium />}
        accent={P.amber}
        action={<><Button variant="outlined" startIcon={<Refresh />} onClick={load} sx={quietButtonSx}>Refresh</Button><Button variant="contained" startIcon={<Add />} onClick={startCreate} sx={primaryButtonSx}>Create plan</Button></>}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,minmax(0,1fr))', lg: 'repeat(4,minmax(0,1fr))' }, gap: 1.05, mb: 1.35 }}>
        <WorkspaceStatCard label="Active plans" value={stats.active} icon={<Subscriptions />} accent={P.blue} hint="Available for registration" />
        <WorkspaceStatCard label="Limited-seat plans" value={stats.limited} icon={<People />} accent={P.purple} hint="Packages with user caps" delay={.03} />
        <WorkspaceStatCard label="Included seats" value={stats.capacity} icon={<People />} accent={P.green} hint="Across active limited plans" delay={.06} />
        <WorkspaceStatCard label="Extra seats enabled" value={stats.extraEnabled} icon={<CheckCircle />} accent={P.amber} hint="Company-priced overage" delay={.09} />
      </Box>

      {loading ? (
        <WorkspacePanel><Box sx={{ minHeight: 310, display: 'grid', placeItems: 'center' }}><CircularProgress size={34} /></Box></WorkspacePanel>
      ) : plans.length === 0 ? (
        <WorkspacePanel accent={P.amber}><WorkspaceEmpty icon={<Subscriptions />} title="No subscription plans yet" text="Create the first company package and set how many users it includes." actionLabel="Create plan" onAction={startCreate} accent={P.amber} /></WorkspacePanel>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2,minmax(0,1fr))', xl: 'repeat(3,minmax(0,1fr))' }, gap: 1.05 }}>
          {plans.map((plan, index) => {
            const accent = [P.blue, P.green, P.purple, P.amber, P.teal, P.coral][index % 6];
            const features = Array.isArray(plan.features) ? plan.features : [];
            return (
              <WorkspacePanel key={plan.id} accent={accent} hover sx={{ p: 1.35, opacity: plan.isActive === false ? .72 : 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', gap: .8, minWidth: 0 }}>
                    <Box sx={{ width: 42, height: 42, flexShrink: 0, borderRadius: '13px', display: 'grid', placeItems: 'center', color: '#fff', background: `linear-gradient(145deg,${accent},${alpha(accent,.72)})`, boxShadow: `0 10px 22px ${alpha(accent,.23)}` }}><Subscriptions fontSize="small" /></Box>
                    <Box sx={{ minWidth: 0 }}><Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap', alignItems: 'center' }}><Typography sx={{ color: P.ink, fontWeight: 870, fontSize: '.88rem' }}>{plan.name}</Typography>{plan.isPopular && <Chip size="small" icon={<Star />} label="Popular" sx={{ bgcolor: alpha(P.amber,.09), color: P.amber, '& .MuiChip-icon': { color: P.amber } }} />}</Box><Typography sx={{ color: P.inkLight, fontSize: '.62rem', mt: .25 }}>{plan.description || 'Company subscription plan'}</Typography></Box>
                  </Box>
                  <Box sx={{ flexShrink: 0 }}><IconButton size="small" onClick={() => startEdit(plan)} sx={{ color: P.blue }}><Edit fontSize="small" /></IconButton><IconButton size="small" onClick={() => remove(plan)} sx={{ color: P.danger }}><Delete fontSize="small" /></IconButton></Box>
                </Box>

                <Box sx={{ mt: 1.15, p: 1, borderRadius: '13px', bgcolor: alpha(accent,.045), border: `1px solid ${alpha(accent,.09)}`, display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-end' }}>
                  <Box><Typography sx={{ color: accent, fontSize: '1.45rem', fontWeight: 900, letterSpacing: '-.04em' }}>{money(plan.price, plan.currency || 'EUR')}</Typography><Typography sx={{ color: P.inkLight, fontSize: '.58rem' }}>per {plan.billingCycle || 'month'}</Typography></Box>
                  <Box sx={{ textAlign: 'right' }}><Typography sx={{ color: P.ink, fontSize: '.72rem', fontWeight: 850 }}>{plan.unlimitedUsers ? 'Unlimited users' : `${Number(plan.userLimit || plan.maxUsers || 1)} included users`}</Typography><Typography sx={{ color: P.inkLight, fontSize: '.58rem', mt: .2 }}>{plan.unlimitedUsers ? 'No seat overage' : plan.allowExtraSeats !== false ? 'Extra paid seats allowed' : 'Hard seat limit'}</Typography></Box>
                </Box>

                <Divider sx={{ my: .95, borderColor: P.hairline }} />
                <Stack spacing={.45} sx={{ minHeight: 74 }}>
                  {features.slice(0, 4).map((feature) => <Box key={feature} sx={{ display: 'flex', gap: .55, alignItems: 'center' }}><CheckCircle sx={{ fontSize: 14, color: accent }} /><Typography sx={{ fontSize: '.62rem', color: P.inkSoft }}>{feature}</Typography></Box>)}
                  {features.length === 0 && <Typography sx={{ fontSize: '.62rem', color: P.inkFaint }}>No package features added yet.</Typography>}
                  {features.length > 4 && <Typography sx={{ fontSize: '.58rem', fontWeight: 750, color: accent }}>+{features.length - 4} more features</Typography>}
                </Stack>
                <Divider sx={{ my: .95, borderColor: P.hairline }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}><FormControlLabel sx={{ m: 0 }} control={<Switch size="small" checked={plan.isActive !== false} onChange={(e) => toggle(plan, e.target.checked)} />} label={<Typography sx={{ fontSize: '.63rem', fontWeight: 800, color: P.inkSoft }}>{plan.isActive !== false ? 'Active' : 'Disabled'}</Typography>} /><StatusChip label={`${features.length} features`} color={accent} /></Box>
              </WorkspacePanel>
            );
          })}
        </Box>
      )}

      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="md" PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ color: P.ink, fontWeight: 850, pb: 1 }}>{editing ? 'Edit subscription plan' : 'Create subscription plan'}<Typography sx={{ color: P.inkLight, fontSize: '.66rem', mt: .25 }}>The included user limit is enforced against companies on this plan. Companies set their own price for extra seats.</Typography></DialogTitle>
        <DialogContent dividers sx={{ borderColor: P.hairline, bgcolor: '#FBFDFE' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2,minmax(0,1fr))' }, gap: 1.1, pt: .15 }}>
            <TextField sx={fieldSx} label="Plan name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <TextField sx={fieldSx} label="Price" type="number" inputProps={{ min: 0, step: .01 }} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            <TextField sx={fieldSx} label="Currency" inputProps={{ maxLength: 3 }} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} />
            <TextField sx={fieldSx} label="Billing cycle" select value={form.billingCycle} onChange={(e) => setForm({ ...form, billingCycle: e.target.value })}><MenuItem value="monthly">Monthly</MenuItem><MenuItem value="quarterly">Quarterly</MenuItem><MenuItem value="annual">Annual</MenuItem><MenuItem value="one-time">One-time</MenuItem></TextField>
            <TextField sx={fieldSx} label="Included user limit" type="number" inputProps={{ min: 1, step: 1 }} disabled={form.unlimitedUsers} value={form.userLimit} onChange={(e) => setForm({ ...form, userLimit: e.target.value })} />
            <TextField sx={fieldSx} label="Sort order" type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} />
          </Box>
          <TextField sx={{ ...fieldSx, mt: 1.1 }} fullWidth label="Description" multiline minRows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <TextField sx={{ ...fieldSx, mt: 1.1 }} fullWidth label="Features (one per line)" multiline minRows={5} value={form.featuresText} onChange={(e) => setForm({ ...form, featuresText: e.target.value })} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,minmax(0,1fr))' }, gap: .6, mt: 1.05 }}>
            <FormControlLabel control={<Switch checked={Boolean(form.unlimitedUsers)} onChange={(e) => setForm({ ...form, unlimitedUsers: e.target.checked, allowExtraSeats: e.target.checked ? false : form.allowExtraSeats })} />} label={<Typography sx={{ fontSize: '.66rem', fontWeight: 750 }}>Unlimited users</Typography>} />
            <FormControlLabel control={<Switch checked={Boolean(form.allowExtraSeats && !form.unlimitedUsers)} disabled={form.unlimitedUsers} onChange={(e) => setForm({ ...form, allowExtraSeats: e.target.checked })} />} label={<Typography sx={{ fontSize: '.66rem', fontWeight: 750 }}>Allow extra paid seats</Typography>} />
            <FormControlLabel control={<Switch checked={Boolean(form.isPopular)} onChange={(e) => setForm({ ...form, isPopular: e.target.checked })} />} label={<Typography sx={{ fontSize: '.66rem', fontWeight: 750 }}>Popular plan</Typography>} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 1.4 }}><Button onClick={() => setOpen(false)} sx={quietButtonSx}>Cancel</Button><Button variant="contained" startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <Save />} disabled={saving} onClick={save} sx={primaryButtonSx}>Save plan</Button></DialogActions>
      </Dialog>
    </WorkspacePage>
  );
}
